/**
 * The app's single URL-scheme policy, in its own DOM-free module.
 *
 * Split out of `security.js` (which re-exports both functions, so every
 * existing import keeps working) because that module installs a DOMPurify hook
 * at import time: any consumer of the scheme check would otherwise drag
 * DOMPurify into its chunk and break in a plain `node` test environment. Pure
 * consumers — the Markdown export serializer, workers — import this file.
 */

/**
 * http(s), optionally mailto, and relative/anchor/query URLs are renderable;
 * javascript:, data:, vbscript: and every other explicit scheme are not.
 *
 * Browsers strip ASCII whitespace/control chars from a URL before parsing its
 * scheme (so `java\tscript:` becomes javascript:), so the scheme is detected on
 * a stripped + lowercased copy to close that bypass.
 *
 * @param {unknown} href
 * @param {{ allowMailto?: boolean }} [options]
 * @returns {boolean}
 */
export function isSafeHref(href, { allowMailto = false } = {}) {
  if (!href || typeof href !== 'string') return false
  const probe = href.trim().replace(/[\u0000-\u0020]/g, '').toLowerCase()
  if (!probe) return false
  if (/^(?:[/#?]|https?:)/.test(probe)) return true
  if (allowMailto && probe.startsWith('mailto:')) return true
  if (/^[a-z][a-z0-9+.-]*:/.test(probe)) return false
  return true
}

/**
 * `:href` binding guard for author-supplied link URLs on shared/public/embed
 * surfaces, where `link.url` is ANOTHER user's data and may be a stored
 * `javascript:`/`data:` scheme (the API accepts any string on save). Returns
 * the URL when isSafeHref passes, else null — binding `:href="safeHref(url)"`
 * makes Vue omit the attribute entirely, so a hostile URL renders as an inert,
 * non-navigable element instead of a clickable script link. A `javascript:`
 * href executes on click regardless of `target` or `rel`, so it must never
 * reach the DOM. Mirrors functions/_middleware.js safeUrl() for the SSR copy.
 *
 * @param {unknown} url
 * @param {{ allowMailto?: boolean }} [options]
 * @returns {string|null}
 */
export function safeHref(url, options = {}) {
  return isSafeHref(url, options) ? url : null
}

/**
 * `src` guard for author-supplied IMAGE urls that are written into the DOM.
 *
 * Stricter than `isSafeHref` on purpose: an <img> only ever needs http(s), so
 * relative/scheme-less strings are rejected too rather than resolved against
 * the app origin. Parsing with `new URL` (instead of a regex) means the
 * browser's own scheme normalization decides — control-char bypasses like
 * `java\tscript:` fail to parse rather than sneaking past a pattern.
 *
 * Returns the parsed absolute URL (safe to assign to `img.src`) or null.
 *
 * @param {unknown} url
 * @returns {string|null}
 */
export function safeImageSrc(url) {
  if (!url || typeof url !== 'string') return null
  try {
    const parsed = new URL(url.trim())
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null
  } catch {
    return null
  }
}
